import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';
import ApiClient from '../loopback/apiClient';
import {
  addOrUpdateDigitalSubmission,
  fetchDigitalGroundTruthGroups,
  fetchDigitalGroundTruths,
  fetchDigitalSectionScores,
  fetchDigitalSubmissionsWithMetas,
  fetchDigitalSurveyScores,
  fetchFullDigitalSurvey,
  fetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta,
  setDigitalSubmissionMeta,
  setSelectedCase,
  setSelectedDigitalsection,
  setSelectedDigitalGroundTruthGroup,
  setSurveySubmissionMeta,
} from '../redux/actions';
import {
  selectDigitalCases,
  selectDigitalQuestionsBySection,
  selectSelectedCase,
  selectSelectedSection,
  selectDigitalSections,
  selectDigitalSubmissionByQuestionAndCase,
  selectDigitalSubmissionMetaBySection,
  selectDigitalSurveyScoreBasedOnGTGroupId,
  selectSectionScoreBasedOnSectionAndGTGroupId,
  selectContextSubmissionByQuestion,
  selectContextQuestionBySection,
  selectSettingsDictionaryByQuestionAndCase,
  selectDigitalGTDictionaryByQuestionAndCase,
} from '../redux/reducers/SurveyDataReducer';
import {
  DigitalSubmission,
  DigitalSubmissionMeta,
  RootState,
  SurveySubmissionMeta,
} from '../redux/types';
import ReviewSubmissionTable from '../components/review/ReviewSubmissionTable';
import ReviewCommentEditor from '../components/review/ReviewCommentEditor';
import GenericDialog from '../components/GenericDialog';
import ContextQuestionInputForm from '../components/survey/questionnaire/ContextQuestionInputForm';
import FaerbungReviewEditor from '../components/review/reviewCategory/faerbungReview/FaerbungReviewEditor';
import useGetTrialById from '../hooks/trial/useGetTrialById';
import { Colors } from '../components/utilities/Colors';
import TextblockReviewEditor from '../components/review/reviewCategory/textblockReview/TextblockReviewEditor';
import {
  selectFaerbungOverallPassScore,
  selectFaerbungReviewScoreBySectionId,
  selectSelectedFaerbungReviewMeta,
  selectTotalFaerbungReviewScore,
} from '../redux/reducers/FaerbungReviewReducer';
import { convertToQid } from '../utilities/stringHelper';
import { EnrollmentWithInstitute } from '../loopback/apiClient/EnrollmentApi';

type RouteProps = {
  trialId: string;
  enrollmentId: string;
}
const PassChip: React.FC = () => (<Chip color="success" size="small" label={<FormattedMessage id="pass" />} />);
const FailChip: React.FC = () => (<Chip color="error" size="small" label={<FormattedMessage id="fail" />} />);

const ReviewView: React.FC<Props> = ({
  addOrUpdateDigitalSubmission: dispatchAddOrUpdateDigitalSubmission,
  cases,
  digitalSectionScore,
  digitalSurveyScore,
  fetchDigitalGroundTruthGroups: dispatchFetchDigitalGroundTruthGroups,
  fetchDigitalGroundTruths: dispatchFetchDigitalGroundTruths,
  fetchDigitalSectionScores: dispatchFetchDigitalSectionScores,
  fetchDigitalSubmissionsWithMetas: dispatchFetchDigitalSubmissionsWithMeta,
  fetchDigitalSurveyScores: dispatchFetchDigitalSurveyScores,
  fetchFullDigitalSurvey: dispatchFetchFullDigitalSurvey,
  fetchSurveySubmissionMeta: dispatchFetchSurveySubmissionMeta,
  intl,
  questions,
  contextQuestions,
  sectionSubmissionMeta,
  sections,
  selectedCase,
  selectedSection,
  setDigitalSubmissionMeta: dispatchSetDigitalSubmissionMeta,
  setSelectedCase: dispatchSetSelectedCase,
  setSelectedDigitalGroundTruthGroup: dispatchSetSelectedDigitalGroundTruthGroup,
  setSelectedDigitalsection: dispatchSetSelectedDigitalSection,
  setSurveySubmissionMeta: dispatchSetSurveySubmissionMeta,
  submissionMetas,
  submissionsMapByQuestionAndCaseId,
  contextSubmissionMapByQuestionId,
  survey,
  surveySubmissionMeta,
  settingsDictionary,
  gtDictionaryByQuestionAndCase,
  faerbungReviewMeta,
  faerbungReviewSectionScore,
  totalFaerbungReviewScore,
  overallFaerbungReviewPassScore,
}: Props) => {
  const { trialId, enrollmentId } = useParams<RouteProps>();
  const [enrollment, setEnrollment] = useState<EnrollmentWithInstitute>();
  const [showMethodicalQuestion, setShowMethodicalQuestion] = useState<boolean>(false);
  const trial = useGetTrialById(Number(trialId));

  useEffect(() => {
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((surveys) => surveys.filter((ds) => !ds.exercise))
      .then((selectedSurvey) => dispatchFetchFullDigitalSurvey(selectedSurvey[0].id));
    ApiClient.getEnrollmentsWithInstitute(Number(trialId)).then((e) => {
      const enrollmentObj = e.find((en) => en.id === Number(enrollmentId));
      if (enrollmentObj) {
        setEnrollment(enrollmentObj);
        dispatchFetchSurveySubmissionMeta(enrollmentObj.id);
        dispatchFetchDigitalSubmissionsWithMeta(enrollmentObj.id);
      }
    });
  }, [
    dispatchFetchDigitalSubmissionsWithMeta,
    dispatchFetchFullDigitalSurvey,
    dispatchFetchSurveySubmissionMeta,
    dispatchSetDigitalSubmissionMeta,
    enrollmentId,
    trialId,
  ]);

  useEffect(() => {
    const fetchGTGroups = async () => {
      if (survey?.id && enrollment) {
        await dispatchFetchDigitalGroundTruthGroups(survey.id);
        await dispatchFetchDigitalGroundTruths(survey.id);
        await dispatchFetchDigitalSurveyScores(survey.id);
        await dispatchFetchDigitalSectionScores(survey.id);
        await dispatchSetSelectedDigitalGroundTruthGroup(
          enrollment.digitalGroundTruthGroupId || null,
        );
      }
    };

    fetchGTGroups();
  }, [
    dispatchFetchDigitalGroundTruthGroups,
    dispatchFetchDigitalGroundTruths,
    dispatchFetchDigitalSectionScores,
    dispatchFetchDigitalSurveyScores,
    dispatchSetSelectedDigitalGroundTruthGroup,
    enrollment,
    survey?.id,
  ]);

  useEffect(() => {
    if (!selectedSection && sections.length > 0) {
      dispatchSetSelectedDigitalSection(sections[0].id);
    } else {
      dispatchSetSelectedCase(undefined);
    }
  }, [selectedSection, dispatchSetSelectedDigitalSection, dispatchSetSelectedCase, sections]);

  useEffect(() => {
    if (!selectedCase && cases.length > 0) {
      dispatchSetSelectedCase(cases[0].id);
    }
  }, [selectedSection, selectedCase, dispatchSetSelectedCase, cases]);

  const handleReviewTabSwitch = (event: React.SyntheticEvent, newValue: number) => {
    dispatchSetSelectedDigitalSection(newValue);
  };

  const onSaveReviewScore = async (updatedSubmission: DigitalSubmission) => {
    const savedSubmission = await ApiClient.updateReviewScore(updatedSubmission);
    dispatchAddOrUpdateDigitalSubmission(savedSubmission);
    if (enrollment && survey) {
      const [dsm, ssm] = await ApiClient.evaluateReviewScore(
        savedSubmission.enrollmentId,
        savedSubmission.digitalSectionId,
        survey.id,
        savedSubmission.digitalQuestionId,
      );
      const updatedMetas = Object.values(submissionMetas)
        .map((meta) => (dsm.id === meta.id ? dsm : meta));
      dispatchSetDigitalSubmissionMeta(updatedMetas);
      dispatchSetSurveySubmissionMeta(ssm);
    }
  };

  const onSaveSectionSubmissionMeta = async (dsm: DigitalSubmissionMeta) => {
    const updatedMetas = Object.values(submissionMetas)
      .map((meta) => (dsm.id === meta.id ? dsm : meta));
    dispatchSetDigitalSubmissionMeta(updatedMetas);
  };

  const onSaveSurveySubmissionMeta = async (ssm: SurveySubmissionMeta) => {
    dispatchSetSurveySubmissionMeta(ssm);
  };

  const { reviewScore: surveyReviewScore } = surveySubmissionMeta || {};
  const { reviewScore: sectionReviewScore } = sectionSubmissionMeta || {};

  const { passScore: sectionPassScore } = digitalSectionScore || {};
  const { passScore: digitalSurveyPassScore } = digitalSurveyScore || {};
  const { sampleset, attendeeId = 0, institute } = enrollment || {};
  return (
    <Box sx={{ overflow: 'auto' }}>
      <Typography variant="h4" sx={{ mx: 2, mt: 2 }}>Review</Typography>
      <Typography variant="h6" sx={{ mx: 2 }}>
        <FormattedMessage
          id="review.submissionHeadline"
          values={{
            user: sampleset
              // should always have an institue, and should have a country,
              // so no need to pass in the user country
              || `${convertToQid(attendeeId, institute ? institute.country : undefined)}`,
          }}
        />
      </Typography>
      {
        !surveySubmissionMeta?.finalized && (
          <Alert severity="warning" sx={{ m: 2 }}>
            <FormattedMessage id="review.notfinalized" />
          </Alert>
        )
      }
      {
        selectedSection && (
          <Box sx={{ mt: 4 }}>
            <Tabs
              value={selectedSection?.id}
              onChange={handleReviewTabSwitch}
              variant="scrollable"
              scrollButtons
            >
              {
                sections.map((s) => <Tab label={s.name} key={s.id} value={s.id} />)
              }
            </Tabs>
          </Box>
        )
      }
      {
        selectedSection?.optional && (
          <Alert sx={{ mt: 1 }} severity="info">This is an Optional Section and its marks will have no influence on the overall survey score or result</Alert>
        )
      }
      <Box sx={{ height: '100%', p: 2 }}>
        <ReviewSubmissionTable
          mode="review"
          digitalCases={cases}
          digitalQuestions={questions}
          submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
          onSave={onSaveReviewScore}
          showLegend
          viewOnly={!surveySubmissionMeta?.finalized}
          settingsDictionary={settingsDictionary}
          DGTDictionaryByQuestionAndCase={gtDictionaryByQuestionAndCase}
          DialogRenderElement={
            sectionSubmissionMeta && surveySubmissionMeta && (
              <ReviewCommentEditor
                sectionSubmissionMeta={sectionSubmissionMeta}
                surveySubmissionMeta={surveySubmissionMeta}
                updateDSM={onSaveSectionSubmissionMeta}
                updateSSM={onSaveSurveySubmissionMeta}
                hideSectionCommentInput
              />
            )
          }
        />
      </Box>
      <Box sx={{
        my: 2,
        py: 2,
        px: 4,
        backgroundColor: 'grey.100',
      }}
      >
        <Box display="flex" gap={10}>
          <Box>
            <Typography fontWeight={500}>
              <FormattedMessage id="review.minScoreHeadline" />
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography>
                <FormattedMessage id="section" />
                {` ${sectionPassScore}`}
              </Typography>
            </Box>
            <Typography>
              <FormattedMessage id="overall" />
              {` ${digitalSurveyPassScore}`}
            </Typography>
            {trial?.reviewCategoryId === 2 && (
              <Box component="span">
                <Typography>
                  <FormattedMessage id="review.sectionStainingReview" />
                  {faerbungReviewMeta?.passingScore}
                </Typography>
                <Typography>
                  <FormattedMessage id="review.surveyStainingReview" />
                  {overallFaerbungReviewPassScore}
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <Typography fontWeight={500}>
              <FormattedMessage id="review.obtainedScoreHeadline" />
            </Typography>
            <Typography>
              <FormattedMessage id="section" />
              {` ${sectionSubmissionMeta?.acquiredScore}`}
            </Typography>
            <Typography>
              <FormattedMessage id="overall" />
              {` ${surveySubmissionMeta?.acquiredScore}`}
            </Typography>
          </Box>
          <Box>
            <Typography fontWeight={500}>
              <FormattedMessage id="review.reviewScoreHeadline" />
            </Typography>
            <Typography>
              <FormattedMessage id="section" />
              {` ${sectionReviewScore || intl.formatMessage({ id: 'review.noReview' })}`}
            </Typography>
            <Typography>
              <FormattedMessage id="overall" />
              {` ${surveyReviewScore || intl.formatMessage({ id: 'review.noReview' })}`}
            </Typography>
            {trial?.reviewCategoryId === 2 && (
              <Box component="span">
                <Typography>
                  <FormattedMessage id="review.sectionStainingReview" />
                  {faerbungReviewSectionScore}
                </Typography>
                <Typography>
                  <FormattedMessage id="review.surveyStainingReview" />
                  {totalFaerbungReviewScore}
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <Typography fontWeight={500}>Status</Typography>
            <Box sx={{ mb: '0.1em' }}>
              <Typography component="span"><FormattedMessage id="review.sectionStatus" /></Typography>
              <Typography fontWeight={500} color={sectionSubmissionMeta?.passed ? Colors.correct : Colors.wrong} component="span">
                {sectionSubmissionMeta?.passed ? <PassChip /> : <FailChip />}
              </Typography>
            </Box>
            <Box sx={{ mb: '0.1em' }}>
              <Typography component="span"><FormattedMessage id="review.overallStatus" /></Typography>
              <Typography fontWeight={500} component="span" color={surveySubmissionMeta?.passed ? Colors.correct : Colors.wrong}>
                {surveySubmissionMeta?.passed ? <PassChip /> : <FailChip />}
              </Typography>
            </Box>
          </Box>
          <Tooltip
            title={!contextQuestions || contextQuestions.length === 0 ? <FormattedMessage id="review.noOpenQuestions" /> : ''}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="primary"
                disabled={!contextQuestions || contextQuestions.length === 0}
                onClick={() => setShowMethodicalQuestion(true)}
              >
                <FormattedMessage id="review.showOpenQuestions" />
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <GenericDialog
        onRequestClose={() => setShowMethodicalQuestion(false)}
        isOpen={showMethodicalQuestion}
      >
        <Box sx={{ p: 3, minHeight: '75vh' }}>
          <Typography variant="h6" sx={{ mt: 2, ml: 2 }}><FormattedMessage id="review.methodicalQuestionTitle" /></Typography>
          <ContextQuestionInputForm
            trialId={Number(trialId)}
            sectionId={selectedSection?.id || 0}
            enrollmentId={Number(enrollmentId)}
            questions={contextQuestions}
            contextSubmissionByQuestionId={contextSubmissionMapByQuestionId}
            disabled
          />
        </Box>
      </GenericDialog>
      <Box sx={{ m: 2 }}>
        {
          sectionSubmissionMeta && surveySubmissionMeta && (
            <ReviewCommentEditor
              sectionSubmissionMeta={sectionSubmissionMeta}
              surveySubmissionMeta={surveySubmissionMeta}
              updateDSM={onSaveSectionSubmissionMeta}
              updateSSM={onSaveSurveySubmissionMeta}
            />
          )
        }
      </Box>
      <Box sx={{ m: 2 }}>
        {
          trial?.reviewCategoryId === 2 && selectedSection && survey && (
            <FaerbungReviewEditor
              caseList={cases}
              surveyId={survey.id}
              sectionId={selectedSection.id}
              enrollmentId={Number(enrollmentId)}
            />
          )
        }
        {
          trial?.reviewCategoryId === 3 && selectedSection && (
            <TextblockReviewEditor
              trialId={trial.id}
              selectedSection={selectedSection}
              sections={sections}
              enrollmentId={Number(enrollmentId)}
            />
          )
        }
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  cases: selectDigitalCases(state),
  digitalSectionScore: selectSectionScoreBasedOnSectionAndGTGroupId(state),
  digitalSurveyScore: selectDigitalSurveyScoreBasedOnGTGroupId(state),
  questions: selectDigitalQuestionsBySection(state),
  sectionSubmissionMeta: selectDigitalSubmissionMetaBySection(state),
  sections: selectDigitalSections(state),
  selectedCase: selectSelectedCase(state),
  selectedSection: selectSelectedSection(state),
  submissionMetas: state.SurveyData.digitalSubmissionMeta.byId,
  submissionsMapByQuestionAndCaseId: selectDigitalSubmissionByQuestionAndCase(state),
  contextSubmissionMapByQuestionId: selectContextSubmissionByQuestion(state),
  contextQuestions: selectContextQuestionBySection(state),
  survey: state.SurveyData.survey,
  surveySubmissionMeta: state.SurveyData.surveySubmissionMeta,
  settingsDictionary: selectSettingsDictionaryByQuestionAndCase(state),
  gtDictionaryByQuestionAndCase: selectDigitalGTDictionaryByQuestionAndCase(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  faerbungReviewMeta: selectSelectedFaerbungReviewMeta(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  faerbungReviewSectionScore: selectFaerbungReviewScoreBySectionId(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  totalFaerbungReviewScore: selectTotalFaerbungReviewScore(state),
  overallFaerbungReviewPassScore: selectFaerbungOverallPassScore(state),
});

const dispatchProps = {
  addOrUpdateDigitalSubmission,
  fetchDigitalGroundTruthGroups,
  fetchDigitalGroundTruths,
  fetchDigitalSectionScores,
  fetchDigitalSubmissionsWithMetas,
  fetchDigitalSurveyScores,
  fetchFullDigitalSurvey,
  fetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta,
  setDigitalSubmissionMeta,
  setSelectedCase,
  setSelectedDigitalGroundTruthGroup,
  setSelectedDigitalsection,
  setSurveySubmissionMeta,
};
const connector = connect(mapStateToProps, dispatchProps);
type Props = ConnectedProps<typeof connector> & {
  intl: {
    formatMessage: (arg: Record<string, string>) => string;
  }
};

export default injectIntl(connector(ReviewView));
